import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import {
  Users, Plus, X, Loader2, AlertTriangle, CheckCircle2, Shield, Trash2, ToggleLeft, ToggleRight,
  Edit2, Check, KeyRound, Copy,
} from "lucide-react";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin", color: "#378ADD" },
  { value: "super_admin", label: "Super Admin (org)", color: "#7F77DD", planRequired: ["enterprise", "test"] },
  { value: "contributor", label: "Contributeur", color: "#1D9E75" },
  { value: "reader", label: "Lecteur", color: "#A1A1AA" },
];

function fmtErr(d) {
  if (!d) return "Erreur.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

function RoleBadge({ role }) {
  const meta = ROLE_OPTIONS.find((r) => r.value === role) || { label: role, color: "#A1A1AA" };
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium border"
      style={{ background: `${meta.color}1A`, color: meta.color, borderColor: `${meta.color}55` }}>
      {meta.label}
    </span>
  );
}

export default function WorkspaceUsers() {
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftRole, setDraftRole] = useState("");
  const [tempCred, setTempCred] = useState(null); // {email, password}

  const load = async () => {
    try {
      const [m, u] = await Promise.all([
        api.get("/workspace/me"),
        api.get("/workspace/users"),
      ]);
      setMe(m.data);
      setData(u.data);
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const plan = me?.organisation?.plan || "test";
  const myRole = me?.user?.role;
  const canManage = myRole === "admin" || myRole === "super_admin" || me?.user?.is_ultra_admin;

  const startEdit = (u) => { setEditingId(u.id); setDraftRole(u.role); };
  const saveRole = async (u) => {
    try {
      await api.patch(`/workspace/users/${u.id}`, { role: draftRole });
      setEditingId(null);
      await load();
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/workspace/users/${u.id}`, { active: !u.active }); await load(); }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Retirer ${u.email} de l'organisation ? Ses contributions seront anonymisées en "Ancien collaborateur".`)) return;
    try { await api.delete(`/workspace/users/${u.id}`); await load(); }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  const resetPassword = async (u) => {
    if (!window.confirm(`Générer un nouveau mot de passe temporaire pour ${u.email} ?`)) return;
    try {
      const r = await api.post(`/workspace/users/${u.id}/reset-password`);
      setTempCred({ email: u.email, password: r.data.temp_password });
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };

  if (!canManage && me) {
    return (
      <div className="space-y-6" data-testid="workspace-users-forbidden">
        <div className="flex items-center gap-2 text-[#EF9F27] noam-overline"><Shield className="w-3 h-3" /> Accès restreint</div>
        <h1 className="noam-display text-2xl font-bold mt-1">Réservé aux admins de l'organisation</h1>
        <p className="text-zinc-400 text-sm">Demandez à un Admin de vous donner les droits de gestion des utilisateurs.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workspace-users-root">
      <div>
        <div className="flex items-center gap-2 text-[#378ADD] noam-overline"><Users className="w-3 h-3" /> Gestion des utilisateurs</div>
        <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">Utilisateurs de l'organisation</h1>
        <p className="text-zinc-400 text-sm mt-1">Inviter, modifier le rôle, désactiver ou retirer les membres de votre environnement.</p>
      </div>

      <div className="flex justify-end">
        <button onClick={() => setShowInvite(true)} className="btn-primary text-xs" data-testid="ws-users-invite-btn">
          <Plus className="w-3.5 h-3.5" /> Inviter un utilisateur
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      <div className="noam-card p-0 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Nom</div>
          <div className="col-span-2">Rôle</div>
          <div className="col-span-1 text-center">Statut</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {!data && (
          <div className="px-5 py-6 text-zinc-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        )}
        {data?.items?.length === 0 && (
          <div className="px-5 py-10 text-zinc-500 text-sm text-center italic">Aucun utilisateur.</div>
        )}
        {data?.items?.map((u) => {
          const editing = editingId === u.id;
          const isMe = u.id === me?.user?.id;
          return (
            <div key={u.id} className="grid grid-cols-12 gap-3 items-center px-5 py-3 border-b border-white/5 text-sm hover:bg-white/[0.02]" data-testid={`ws-user-row-${u.id}`}>
              <div className="col-span-4 truncate">
                <span className="text-white">{u.email}</span>
                {isMe && <span className="ml-2 text-[10px] text-[#378ADD]">(vous)</span>}
              </div>
              <div className="col-span-2 text-zinc-400 truncate">{u.name || "—"}</div>
              <div className="col-span-2">
                {editing ? (
                  <select value={draftRole} onChange={(e) => setDraftRole(e.target.value)} className="noam-input text-xs">
                    {ROLE_OPTIONS.filter((r) => !r.planRequired || r.planRequired.includes(plan)).map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                ) : (
                  <RoleBadge role={u.role} />
                )}
              </div>
              <div className="col-span-1 text-center">
                {u.active ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#1D9E75]"><CheckCircle2 className="w-3 h-3" /> actif</span>
                ) : (
                  <span className="text-[10px] text-zinc-500">désactivé</span>
                )}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-1">
                {editing ? (
                  <>
                    <button onClick={() => saveRole(u)} className="text-[#1D9E75] p-1.5 rounded hover:bg-white/5" title="Enregistrer" data-testid={`ws-user-save-${u.id}`}>
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-zinc-500 p-1.5 rounded hover:bg-white/5" title="Annuler">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => resetPassword(u)} className="text-zinc-400 hover:text-[#EF9F27] p-1.5 rounded hover:bg-white/5" title="Réinitialiser le mot de passe" data-testid={`ws-user-reset-${u.id}`}>
                      <KeyRound className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => startEdit(u)} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5" title="Modifier le rôle" data-testid={`ws-user-edit-${u.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => toggleActive(u)} disabled={isMe && u.active} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5 disabled:opacity-30" title={u.active ? "Désactiver" : "Activer"} data-testid={`ws-user-toggle-${u.id}`}>
                      {u.active ? <ToggleRight className="w-4 h-4 text-[#1D9E75]" /> : <ToggleLeft className="w-4 h-4" />}
                    </button>
                    <button onClick={() => remove(u)} disabled={isMe} className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5 disabled:opacity-30" title="Retirer de l'organisation" data-testid={`ws-user-delete-${u.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showInvite && (
        <InviteUserModal
          plan={plan}
          onClose={() => setShowInvite(false)}
          onCreated={(created) => {
            setShowInvite(false);
            if (created?.temp_password) setTempCred({ email: created.email, password: created.temp_password });
            load();
          }}
        />
      )}

      {tempCred && <TempCredModal cred={tempCred} onClose={() => setTempCred(null)} />}
    </div>
  );
}

function InviteUserModal({ plan, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", role: "contributor", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.post("/workspace/users", {
        email: form.email.trim(),
        role: form.role,
        password: form.password.trim() || undefined,
        name: form.name.trim() || undefined,
      });
      onCreated(r.data);
    } catch (err) { setError(fmtErr(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" testid="ws-invite-modal">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="noam-overline">Nouveau membre</div>
          <div className="noam-display text-base font-semibold mt-0.5">Inviter dans l'organisation</div>
          <p className="text-[11px] text-zinc-500 mt-1.5">Si le mot de passe est laissé vide, un mot de passe temporaire sera généré.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      <form onSubmit={submit} className="space-y-3.5">
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Email</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoFocus
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="ws-invite-email" />
        </div>
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Rôle</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="ws-invite-role">
            {ROLE_OPTIONS.filter((r) => !r.planRequired || r.planRequired.includes(plan)).map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nom (facultatif)</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="ws-invite-name" />
        </div>
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Mot de passe (auto-généré si vide)</label>
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="auto-généré"
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono focus:border-[#378ADD] focus:outline-none" data-testid="ws-invite-password" />
        </div>
        {error && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="ws-invite-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <>Inviter</>}
        </button>
      </form>
    </Modal>
  );
}

function TempCredModal({ cred, onClose }) {
  const copy = () => navigator.clipboard?.writeText(`${cred.email} / ${cred.password}`).catch(() => {});
  return (
    <Modal onClose={onClose} maxWidth="max-w-md" testid="temp-cred-modal">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="noam-overline text-[#EF9F27]">À copier maintenant</div>
          <div className="noam-display text-base font-semibold mt-0.5">Mot de passe temporaire</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      <div className="rounded-md border border-[#EF9F27]/40 bg-[#EF9F27]/10 p-4 space-y-2">
        <div className="text-[11px] text-[#FCD9A2] uppercase tracking-wider">Email</div>
        <div className="text-white font-mono">{cred.email}</div>
        <div className="text-[11px] text-[#FCD9A2] uppercase tracking-wider mt-3">Mot de passe</div>
        <div className="text-white font-mono break-all">{cred.password}</div>
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Ce mot de passe ne sera plus visible après fermeture. Transmettez-le par un canal sûr.</p>
      <button onClick={copy} className="btn-primary w-full mt-4 justify-center" data-testid="temp-cred-copy"><Copy className="w-3.5 h-3.5" /> Copier email + mot de passe</button>
    </Modal>
  );
}
